// src/pages/admin/AdminAudit.js
// Admin : journal d'audit — 2 onglets
//   • Actions admin  : table audit_logs (activer/desactiver deal, suppression user, impersonation)
//   • Activites      : flux d'activites existant (verificationAPI.adminActivity)
// Maxula

import React, { useEffect, useState, useCallback } from 'react';
import API, { verificationAPI } from '../../services/api';
import { FiToggleRight, FiToggleLeft, FiTrash2, FiLogIn, FiActivity, FiShield } from 'react-icons/fi';

const ACTION_META = {
  'deal.enable': { label: 'Deal activé', cls: 'bg-green-100 text-green-700', Icon: FiToggleRight },
  'deal.disable': { label: 'Deal désactivé', cls: 'bg-red-100 text-red-700', Icon: FiToggleLeft },
  'user.delete': { label: 'Utilisateur supprimé', cls: 'bg-red-100 text-red-700', Icon: FiTrash2 },
  'user.impersonate': { label: 'Connexion en tant que', cls: 'bg-indigo-100 text-indigo-700', Icon: FiLogIn }
};

const ACTION_OPTIONS = [
  { value: '', label: 'Toutes les actions' },
  { value: 'deal.enable', label: 'Deal activé' },
  { value: 'deal.disable', label: 'Deal désactivé' },
  { value: 'user.delete', label: 'Utilisateur supprimé' },
  { value: 'user.impersonate', label: 'Connexion en tant que' }
];

const TYPE_LABELS = {
  deal_create: 'Création annonce',
  deal_update: 'Modification annonce',
  kyc_submit: 'Soumission KYC',
  kyc_approve: 'KYC validé',
  kyc_reject: 'KYC refusé',
  subscription: 'Abonnement',
  login: 'Connexion'
};

const fmtDate = (d) => { try { return new Date(d).toLocaleString('fr-FR'); } catch (e) { return '—'; } };

const Spinner = () => (
  <div className="flex justify-center py-20">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#e11d7b' }} />
  </div>
);

const ActionBadge = ({ action }) => {
  const m = ACTION_META[action];
  if (!m) return <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs text-gray-700">{action || '—'}</span>;
  const Icon = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>
      <Icon size={13} /> {m.label}
    </span>
  );
};

const Pager = ({ page, pages, onPage }) => (
  pages > 1 ? (
    <div className="flex justify-center gap-2 mt-4">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="px-3 py-1.5 rounded border disabled:opacity-50">Préc</button>
      <span className="px-3 py-1.5 text-sm">Page {page} / {pages}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="px-3 py-1.5 rounded border disabled:opacity-50">Suiv</button>
    </div>
  ) : null
);

const AdminAudit = () => {
  const [tab, setTab] = useState('actions');

  // --- onglet Actions (audit_logs) ---
  const [logs, setLogs] = useState([]);
  const [actionFilter, setActionFilter] = useState('');
  const [aPage, setAPage] = useState(1);
  const [aPages, setAPages] = useState(1);
  const [aLoading, setALoading] = useState(true);

  // --- onglet Activites (existant) ---
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const loadLogs = useCallback(async () => {
    try {
      setALoading(true);
      const res = await API.get('/admin/audit-logs', { params: { page: aPage, limit: 50, action: actionFilter || undefined } });
      setLogs(res.data?.data || []);
      setAPages(res.data?.totalPages || 1);
    } catch (e) { setLogs([]); setAPages(1); } finally { setALoading(false); }
  }, [aPage, actionFilter]);

  const loadActivity = useCallback(async () => {
    try {
      setLoading(true);
      const res = await verificationAPI.adminActivity({ page, limit: 50 });
      setItems(res.data?.data || []);
      setPages(res.data?.pages || 1);
    } catch (e) {} finally { setLoading(false); }
  }, [page]);

  useEffect(() => { if (tab === 'actions') loadLogs(); }, [tab, loadLogs]);
  useEffect(() => { if (tab === 'activites') loadActivity(); }, [tab, loadActivity]);

  const tabBtn = (key, label, Icon) => {
    const on = tab === key;
    return (
      <button
        type="button"
        onClick={() => setTab(key)}
        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${on ? 'font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
        style={on ? { color: '#e11d7b', borderColor: '#e11d7b' } : undefined}
      >
        <Icon size={16} /> {label}
      </button>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-1">Journal d'audit</h1>
      <p className="text-gray-500 mb-5 text-sm">Trace des actions sensibles et des activités de la plateforme.</p>

      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabBtn('actions', 'Actions admin', FiShield)}
        {tabBtn('activites', 'Activités', FiActivity)}
      </div>

      {tab === 'actions' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <label className="text-sm text-gray-600">Filtrer :</label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setAPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white"
            >
              {ACTION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          {aLoading ? <Spinner /> : (
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Acteur</th>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Cible</th>
                    <th className="px-4 py-3 text-left">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((l) => (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{fmtDate(l.createdAt)}</td>
                      <td className="px-4 py-3">
                        {l.actorName || '—'}
                        {l.actorRole ? <span className="ml-1 text-xs text-gray-400">({l.actorRole})</span> : null}
                      </td>
                      <td className="px-4 py-3"><ActionBadge action={l.action} /></td>
                      <td className="px-4 py-3 text-gray-700">
                        {l.targetLabel || '—'}
                        {l.targetType ? <span className="ml-1 text-xs text-gray-400">[{l.targetType}{l.targetId ? ` #${l.targetId}` : ''}]</span> : null}
                      </td>
                      <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{l.ip || '—'}</td>
                    </tr>
                  ))}
                  {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">Aucune action enregistrée.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={aPage} pages={aPages} onPage={setAPage} />
        </>
      )}

      {tab === 'activites' && (
        <>
          {loading ? <Spinner /> : (
            <div className="overflow-x-auto bg-white rounded-xl border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Utilisateur</th>
                    <th className="px-4 py-3 text-left">Action</th>
                    <th className="px-4 py-3 text-left">Détail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((a) => (
                    <tr key={a._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500">{fmtDate(a.createdAt)}</td>
                      <td className="px-4 py-3">{a.userId ? `${a.userId.firstName || ''} ${a.userId.lastName || ''}`.trim() || a.userId.email : '—'} <span className="text-xs text-gray-400">{a.role}</span></td>
                      <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs">{TYPE_LABELS[a.type] || a.type}</span></td>
                      <td className="px-4 py-3 text-gray-600">{a.description}</td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-400">Aucune activité.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={page} pages={pages} onPage={setPage} />
        </>
      )}
    </div>
  );
};

export default AdminAudit;
